import { createSlice } from '@reduxjs/toolkit';
import { saveToCache, loadFromCache, saveToCacheWithFilter, CACHE_KEYS } from '../data/cache';

const TAG = '[ovulationTestsSlice]';

const ovulationTestsSlice = createSlice({
  name: 'ovulationTests',
  initialState: {
    selfTests: [],
    otherUsersTests: {},
    listenerUserIds: [],
  },
  reducers: {
    setSelfTests: (state, action) => {
      state.selfTests = action.payload;
      // Cache with TTL
      saveToCacheWithFilter(CACHE_KEYS.TESTS, 'self_tests', action.payload);
    },
    setOtherUsersTests: (state, action) => {
      const { userId, tests } = action.payload;
      state.otherUsersTests[userId] = tests;
      // Cache with TTL
      saveToCacheWithFilter(CACHE_KEYS.TESTS, `tests_${userId}`, tests);
    },
    updateOtherUsersTest: (state, action) => {
      const { userId, test } = action.payload;
      if (!state.otherUsersTests[userId]) {
        state.otherUsersTests[userId] = [test];
      } else {
        // Update or insert test
        const existingIndex = state.otherUsersTests[userId].findIndex(t => t.id === test.id);
        if (existingIndex >= 0) {
          state.otherUsersTests[userId][existingIndex] = test;
        } else {
          state.otherUsersTests[userId].unshift(test);
        }
      }
      // Update cache
      saveToCacheWithFilter(CACHE_KEYS.TESTS, `tests_${userId}`, state.otherUsersTests[userId]);
    },
    addOrUpdateSelfTest: (state, action) => {
      const test = action.payload;
      const existingIndex = state.selfTests.findIndex(t => t.id === test.id);
      if (existingIndex >= 0) {
        state.selfTests[existingIndex] = test;
      } else {
        state.selfTests.unshift(test);
      }
      // Update cache
      saveToCacheWithFilter(CACHE_KEYS.TESTS, 'self_tests', state.selfTests);
    },
    setListenerUserIds: (state, action) => {
      // Only update if the array actually changed (memoization)
      const newIds = [...action.payload].sort();
      const currentIds = [...state.listenerUserIds].sort();
      const idsChanged = JSON.stringify(newIds) !== JSON.stringify(currentIds);
      if (idsChanged) {
        state.listenerUserIds = newIds;
      }
    },
    loadSelfTestsFromCache: (state) => {
      const cached = loadFromCache(CACHE_KEYS.TESTS);
      if (cached?.self_tests?.data) {
        state.selfTests = cached.self_tests.data;
      }
    },
    loadOtherUsersTestsFromCache: (state, action) => {
      const { userId } = action.payload;
      const cached = loadFromCache(CACHE_KEYS.TESTS);
      if (cached?.[`tests_${userId}`]?.data) {
        state.otherUsersTests[userId] = cached[`tests_${userId}`].data;
      }
    },
  },
});

export const {
  setSelfTests,
  addOrUpdateSelfTest,
  setOtherUsersTests,
  updateOtherUsersTest,
  setListenerUserIds,
  loadSelfTestsFromCache,
  loadOtherUsersTestsFromCache,
} = ovulationTestsSlice.actions;

export default ovulationTestsSlice.reducer;
